// assets/AllCourses/Exercises/PictureFillInTheBlanksExercise.jsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { toast } from "sonner";
import { useUploadCourseFileMutation } from "@/features/api/courseApi";
import { cn } from "@/utils/utils";

export default function PictureFillInTheBlanksExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResult = false,
  onComplete = () => {},
  setAttempt = () => {}, // Optional: save progress
}) {
  const isEditMode = typeof onUpdate === "function";
  const [uploadFile] = useUploadCourseFileMutation();
  const [imagePreviews, setImagePreviews] = useState({});

  // === SAFE QUESTIONS ===
  const rawQuestions = exercise?.pictureFillInTheBlanksQuestions;
  const questions = Array.isArray(rawQuestions) ? rawQuestions : [];

  // === STUDENT STATE ===
  const [state, setState] = useState({
    answers: [], // Will be: questions.map(q => array of user inputs for blanks)
    hasInteracted: false,
  });

  const completedRef = useRef(false);

  // Initialize or sync student answers safely
  useEffect(() => {
    if (!isPreview) return;

    const numQuestions = questions.length;

    if (numQuestions === 0) {
      setState({ answers: [], hasInteracted: false });
      return;
    }

    // Build default empty answers for each question
    const buildDefaultAnswers = () =>
      questions.map((q) => {
        const blankCount = (q.sentence?.match(/_+/g) || []).length;
        return Array(blankCount).fill("");
      });

    // If no saved attempt → fresh start
    if (!exercise.attempt) {
      const defaultAnswers = buildDefaultAnswers();
      setState({ answers: defaultAnswers, hasInteracted: false });
      setAttempt({ answers: defaultAnswers, hasInteracted: false });
      return;
    }

    // Handle legacy format: attempt.answers = [["sun"], ["sky"]]
    if (Array.isArray(exercise.attempt.answers)) {
      const legacy = exercise.attempt.answers;
      const synced = questions.map((q, i) => {
        const blankCount = (q.sentence?.match(/_+/g) || []).length;
        const old = legacy[i] || [];
        return Array.from({ length: blankCount }, (_, j) => old[j] || "");
      });
      setState({ answers: synced, hasInteracted: !!exercise.attempt.hasInteracted });
      setAttempt({ answers: synced, hasInteracted: !!exercise.attempt.hasInteracted });
      return;
    }

    // New format: attempt.answers should be 2D array
    if (Array.isArray(exercise.attempt.answers)) {
      // Already good, but ensure length matches
      const synced = questions.map((q, i) => {
        const blankCount = (q.sentence?.match(/_+/g) || []).length;
        const saved = exercise.attempt.answers[i] || [];
        return Array.from({ length: blankCount }, (_, j) => saved[j] || "");
      });
      setState({ answers: synced, hasInteracted: !!exercise.attempt.hasInteracted });
      setAttempt({ answers: synced, hasInteracted: !!exercise.attempt.hasInteracted });
    } else {
      // Fallback: rebuild fresh
      const defaultAnswers = buildDefaultAnswers();
      setState({ answers: defaultAnswers, hasInteracted: false });
      setAttempt({ answers: defaultAnswers, hasInteracted: false });
    }
  }, [isPreview, questions, exercise.attempt, setAttempt]);

  // Reset completion flag on exercise change
  useEffect(() => {
    if (isPreview) completedRef.current = false;
  }, [exercise?._id, isPreview]);

  // Normalize text
  const normalize = (text = "", requireCap = false) => {
    const t = String(text || "").trim();
    return requireCap ? t : t.toLowerCase().replace(/[.,!?]/g, "");
  };

  // Resolve image URL
  const resolveMediaUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    const filename = url.trim().split("/").pop();
    return filename ? `/uploads/image/${filename}` : "";
  };

  // === EDIT MODE ===
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("pictureFillInTheBlanksQuestions", next);
  };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await uploadFile(fd).unwrap();
      return res.fileUrl || res.url || "";
    } catch (err) {
      toast.error("Image upload failed");
      return "";
    }
  };

  const handleQuestionChange = async (qIdx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    let q = next[qIdx] || {
      sentence: "",
      answers: [],
      image: "",
      requireCapitalization: false,
    };

    if (field === "sentence") {
      const blanks = (value.match(/_+/g) || []).length;
      const oldAnswers = Array.isArray(q.answers) ? q.answers : [];
      q = {
        ...q,
        sentence: value,
        answers: Array.from({ length: blanks }, (_, i) => oldAnswers[i] || ""),
      };
    } else {
      q = { ...q, [field]: value };
    }

    next[qIdx] = q;
    updateQuestions(next);
  };

  const handleImageUpload = async (qIdx, file) => {
    if (!isEditMode || !file) return;
    const url = await uploadImage(file);
    if (!url) return;

    const next = [...questions];
    next[qIdx] = { ...next[qIdx], image: url };
    updateQuestions(next);

    setImagePreviews((prev) => ({
      ...prev,
      [qIdx]: resolveMediaUrl(url),
    }));
  };

  const handleRemoveImage = (qIdx) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = { ...next[qIdx], image: "" };
    updateQuestions(next);
    setImagePreviews((prev) => {
      const copy = { ...prev };
      delete copy[qIdx];
      return copy;
    });
  };

  const handleAnswerChange = (qIdx, aIdx, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    const answers = [...(next[qIdx]?.answers || [])];
    answers[aIdx] = value;
    next[qIdx] = { ...next[qIdx], answers };
    updateQuestions(next);
  };

  const handleCapitalizationChange = (qIdx, checked) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = { ...next[qIdx], requireCapitalization: !!checked };
    updateQuestions(next);
  };

  const handleAddQuestion = () => {
    if (!isEditMode) return;
    updateQuestions([
      ...questions,
      { sentence: "", answers: [], image: "", requireCapitalization: false },
    ]);
  };

  const handleRemoveQuestion = (qIdx) => {
    if (!isEditMode || questions.length <= 1) return;
    updateQuestions(questions.filter((_, i) => i !== qIdx));
    setImagePreviews((prev) => {
      const copy = { ...prev };
      delete copy[qIdx];
      return copy;
    });
  };

  // === STUDENT MODE ===
  const handleInputChange = (qIdx, blankIdx, value) => {
    if (isEditMode || showResult) return;

    setState((prev) => {
      const newAnswers = prev.answers.map((qAnswers, i) =>
        i === qIdx
          ? qAnswers.map((a, j) => (j === blankIdx ? value : a))
          : qAnswers
      );

      const nextState = {
        answers: newAnswers,
        hasInteracted: true,
      };

      setAttempt(nextState);

      // Check if all blanks are filled
      const allFilled = newAnswers.every((qAns) => qAns.every((a) => a.trim() !== ""));
      if (allFilled && !completedRef.current) {
        completedRef.current = true;

        const allCorrect = questions.every((q, qi) => {
          return (q.sentence.match(/_+/g) || []).every((_, ai) => {
            const user = normalize(newAnswers[qi][ai], q.requireCapitalization);
            const correct = normalize(q.answers[ai] || "", q.requireCapitalization);
            return user === correct;
          });
        });

        onComplete(exercise._id, allCorrect);
      }

      return nextState;
    });
  };

  // === RENDER: EDIT MODE ===
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No questions yet. Click "Add Question" to begin.
          </p>
        )}

        {questions.map((q, qIdx) => {
          const blanks = (q.sentence?.match(/_+/g) || []).length;
          const preview =
            imagePreviews[qIdx] || (q.image ? resolveMediaUrl(q.image) : "");

          return (
            <Card key={qIdx} className="p-6 space-y-6">
              <TextareaField
                label={`Sentence ${qIdx + 1} (use ___ for blanks)`}
                value={q.sentence || ""}
                onChange={(v) => handleQuestionChange(qIdx, "sentence", v)}
                placeholder="The ___ is shining in the ___."
                rows={4}
                required
              />

              <div className="space-y-3">
                <label className="font-medium">Image (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => e.target.files?.[0] && handleImageUpload(qIdx, e.target.files[0])}
                />
                {preview && (
                  <div className="flex items-center gap-4 mt-3">
                    <img
                      src={preview}
                      alt="Preview"
                      className="w-64 h-48 object-cover rounded border shadow"
                    />
                    <Button variant="destructive" size="sm" onClick={() => handleRemoveImage(qIdx)}>
                      Remove
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <label className="font-medium">Correct Answers</label>
                {blanks === 0 ? (
                  <p className="text-sm text-gray-500">
                    Add underscores (_) in the sentence to create fillable blanks.
                  </p>
                ) : (
                  Array.from({ length: blanks }).map((_, aIdx) => (
                    <StringField
                      key={aIdx}
                      label={`Blank ${aIdx + 1}`}
                      value={q.answers[aIdx] || ""}
                      onChange={(v) => handleAnswerChange(qIdx, aIdx, v)}
                      placeholder="e.g., sun"
                      required
                    />
                  ))
                )}
              </div>

              <CapitalizationRule
                checked={!!q.requireCapitalization}
                onChange={(c) => handleCapitalizationChange(qIdx, c)}
              />

              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleRemoveQuestion(qIdx)}
                disabled={questions.length === 1}
              >
                Remove Question
              </Button>
            </Card>
          );
        })}

        <Button variant="outline" onClick={handleAddQuestion}>
          Add Question
        </Button>
      </div>
    );
  }

  // === RENDER: STUDENT MODE ===
  if (questions.length === 0) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardDescription>No questions available.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardDescription>Look at the picture and fill in the blanks.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-12">
        {questions.map((q, qIdx) => {
          const sentence = q.sentence || "";
          const parts = sentence.split(/_+/g);
          const blanks = parts.length - 1;
          const userAnswers = state.answers[qIdx] || Array(blanks).fill("");

          return (
            <div key={qIdx} className="space-y-8">
              {/* Image */}
              {q.image && (
                <div className="flex justify-center">
                  <img
                    src={resolveMediaUrl(q.image)}
                    alt="Exercise visual"
                    className="max-w-full max-h-96 object-contain rounded-lg border shadow-lg"
                  />
                </div>
              )}

              {/* Sentence with inputs */}
              <div className="text-center text-xl md:text-2xl font-semibold leading-loose flex flex-wrap justify-center items-center gap-3 p-8 bg-muted/50 rounded-xl">
                {parts.map((part, i) => (
                  <React.Fragment key={i}>
                    {part && <span>{part}</span>}
                    {i < blanks && (
                      <Input
                        value={userAnswers[i] || ""}
                        onChange={(e) => handleInputChange(qIdx, i, e.target.value)}
                        placeholder="____"
                        disabled={showResult}
                        className={cn(
                          "w-40 text-center font-bold text-lg",
                          showResult &&
                            normalize(userAnswers[i], q.requireCapitalization) ===
                              normalize(q.answers[i] || "", q.requireCapitalization)
                            ? "border-green-600 bg-green-50"
                            : showResult && userAnswers[i]?.trim()
                            ? "border-red-600 bg-red-50"
                            : ""
                        )}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Result Feedback */}
              {showResult && (
                <div className="text-center space-y-3">
                  {Array.from({ length: blanks }).map((_, i) => {
                    const user = userAnswers[i] || "";
                    const correct = q.answers[i] || "";
                    const isCorrect =
                      normalize(user, q.requireCapitalization) ===
                      normalize(correct, q.requireCapitalization);

                    return (
                      <p
                        key={i}
                        className={cn(
                          "text-lg font-medium",
                          isCorrect ? "text-green-700" : "text-red-700"
                        )}
                      >
                        Blank {i + 1}: {user || "(empty)"} →{" "}
                        {isCorrect ? "Correct!" : `Incorrect — ${correct}`}
                      </p>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}